import React, { useState } from 'react';
import axios from 'axios';
import { AlertTriangle, AlertCircle, Info, Calendar, User, MapPin, CheckCircle, Clock, ChevronDown, ChevronUp, Coffee, Play, RefreshCw } from 'lucide-react';

const ViolationCard = ({ violation, onRespond, onMarkSalaried }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shiftDetails, setShiftDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [markingSalaried, setMarkingSalaried] = useState(false);

  const fetchShiftDetails = async () => {
    if (shiftDetails) return; // Already fetched
    try {
      setLoadingDetails(true);
      const response = await axios.get(`/api/time-audit/violations/${violation.id}`);
      setShiftDetails(response.data);
    } catch (error) {
      console.error('Error fetching shift details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleExpanded = () => {
    if (!isExpanded) {
      fetchShiftDetails();
    }
    setIsExpanded(!isExpanded);
  };

  const handleMarkSalaried = async () => {
    if (!window.confirm(`Mark ${violation.employeeName || violation.employeeId} as salaried? This will dismiss all their pending violations.`)) {
      return;
    }
    setMarkingSalaried(true);
    try {
      await axios.post('/api/time-audit/salaried-employees', {
        employeeId: violation.employeeId,
        locationId: violation.locationId,
        employeeName: violation.employeeName
      });
      if (onMarkSalaried) {
        onMarkSalaried(violation.employeeId);
      }
    } catch (error) {
      console.error('Error marking employee as salaried:', error);
    } finally {
      setMarkingSalaried(false);
    }
  };
  // Severity colors and icons
  const severityConfig = {
    high: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: AlertTriangle,
      badge: 'bg-red-100 text-red-800'
    },
    medium: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-700',
      icon: AlertCircle,
      badge: 'bg-yellow-100 text-yellow-800'
    },
    low: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: Info,
      badge: 'bg-blue-100 text-blue-800'
    },
    warning: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-700',
      icon: AlertCircle,
      badge: 'bg-orange-100 text-orange-800'
    }
  };

  // Violation type display names and explanations
  const violationTypeInfo = {
    meal_break_missing: {
      label: 'Missing Meal Break',
      explanation: 'Employee worked 6 or more hours without taking a 30-minute meal break.'
    },
    meal_break_short: {
      label: 'Short Meal Break',
      explanation: 'Meal break was less than the required 30 minutes.'
    },
    meal_break_late: {
      label: 'Late Meal Break',
      explanation: 'Meal break started after the 5th hour of work.'
    },
    second_meal_missing: {
      label: 'Missing 2nd Meal Break',
      explanation: 'Employee worked over 10 hours without a second 30-minute meal break.'
    },
    second_meal_short: {
      label: 'Short 2nd Meal Break',
      explanation: 'Second meal break was less than the required 30 minutes.'
    },
    second_meal_late: {
      label: 'Late 2nd Meal Break',
      explanation: 'Second meal break started after the 10th hour of work.'
    },
    paid_break_too_long: {
      label: 'Paid Break Too Long',
      explanation: 'A paid break of 30 minutes or more was recorded. This may have been intended as an unpaid meal break.'
    }
  };

  // Status display config
  const statusConfig = {
    pending: { label: 'Pending', color: 'text-yellow-600', bg: 'bg-yellow-100', icon: Clock },
    valid: { label: 'Valid', color: 'text-red-600', bg: 'bg-red-100', icon: AlertTriangle },
    corrected: { label: 'Corrected', color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle }
  };

  const severity = violation.severity || 'medium';
  const status = violation.status || 'pending';

  // Override card styling based on status
  let config;
  if (status === 'corrected') {
    config = {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      icon: CheckCircle,
      badge: 'bg-green-100 text-green-800'
    };
  } else if (status === 'valid') {
    config = {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      icon: AlertTriangle,
      badge: 'bg-red-100 text-red-800'
    };
  } else {
    // Use severity-based styling for pending violations
    config = severityConfig[severity] || severityConfig.medium;
  }
  const SeverityIcon = config.icon;
  const typeInfo = violationTypeInfo[violation.violationType] || {
    label: violation.violationType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    explanation: ''
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format time from string like "HH:MM"
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Format datetime from ISO string. Punch times are stored as store-local
  // wall-clock values with a UTC suffix, so render in UTC to avoid shifting
  // them by the device's offset (matches admin TimeAuditUploads formatTime).
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' });
  };

  // Format duration in minutes
  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Format hours worked
  const formatHours = (hours) => {
    if (!hours) return '';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const statusInfo = statusConfig[status] || statusConfig.pending;
  const StatusIcon = statusInfo.icon;
  const entry = violation.entry || violation.timeClockEntry;

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} overflow-hidden`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2">
            <SeverityIcon className={`h-5 w-5 ${config.text}`} />
            <span className={`font-semibold ${config.text}`}>
              {typeInfo.label}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </div>
        </div>

        {/* Explanation */}
        <p className="text-sm text-gray-600 mb-3">
          {typeInfo.explanation}
        </p>

        {/* Employee & Location Info */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center text-sm text-gray-700">
            <User className="h-4 w-4 mr-2 text-gray-400" />
            <span className="font-medium">{violation.employeeName || entry?.employeeName || 'Unknown Employee'}</span>
            {violation.employeeId && (
              <span className="text-gray-500 ml-2">ID: {violation.employeeId}</span>
            )}
          </div>
          {(violation.location?.locationName || entry?.locationName) && (
            <div className="flex items-center text-sm text-gray-600">
              <MapPin className="h-4 w-4 mr-2 text-gray-400" />
              <span>
                {violation.location?.locationNumber && `${violation.location.locationNumber} - `}
                {violation.location?.locationName || entry?.locationName}
              </span>
            </div>
          )}
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="h-4 w-4 mr-2 text-gray-400" />
            <span>{formatDate(violation.workDate || entry?.workDate)}</span>
          </div>
          {/* Mark as Salaried */}
          {status === 'pending' && (
            <button
              onClick={handleMarkSalaried}
              disabled={markingSalaried}
              className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-50 mt-1"
            >
              <User className="h-3.5 w-3.5" />
              {markingSalaried ? 'Marking...' : 'Mark as Salaried (Exempt)'}
            </button>
          )}
        </div>

        {/* Expandable Shift Details Button */}
        <button
          onClick={toggleExpanded}
          className="w-full flex items-center justify-between p-3 bg-white bg-opacity-60 rounded-md mb-3 hover:bg-opacity-80 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">View Shift Timeline</span>
          </div>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </button>

        {/* Expanded Shift Details */}
        {isExpanded && (
          <div className="bg-white rounded-lg border border-gray-200 p-3 mb-3">
            {loadingDetails ? (
              <div className="text-center py-4">
                <RefreshCw className="h-5 w-5 animate-spin mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">Loading shift details...</p>
              </div>
            ) : shiftDetails?.shiftDetails ? (
              <div className="space-y-3">
                {/* Shift Overview */}
                <div className="flex flex-wrap gap-4 text-sm pb-3 border-b border-gray-100">
                  <div>
                    <span className="text-gray-500">Shift:</span>
                    <span className="ml-1 font-medium">
                      {formatDateTime(shiftDetails.shiftDetails.shiftStart)} - {formatDateTime(shiftDetails.shiftDetails.shiftEnd)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total:</span>
                    <span className="ml-1 font-medium">{formatHours(violation.shiftHours)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Break:</span>
                    <span className="ml-1 font-medium">{formatDuration(shiftDetails.shiftDetails.totalBreakMinutes)}</span>
                  </div>
                </div>

                {/* Violation Details Box */}
                {shiftDetails.details && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-2">
                    <h5 className="text-xs font-semibold text-red-800 mb-1">Violation Details</h5>
                    <div className="text-xs text-red-700 space-y-0.5">
                      {shiftDetails.details.requiredByHour && (
                        <div>Break required by hour: <strong>{shiftDetails.details.requiredByHour}</strong></div>
                      )}
                      {shiftDetails.details.breakStartedAtHour && (
                        <div>Break started at hour: <strong>{shiftDetails.details.breakStartedAtHour.toFixed(1)}</strong></div>
                      )}
                      {shiftDetails.details.actualBreakMinutes !== undefined && (
                        <div>Break duration: <strong>{shiftDetails.details.actualBreakMinutes.toFixed(0)}min</strong> (required: {shiftDetails.details.requiredMinutes}min)</div>
                      )}
                      {shiftDetails.details.totalHoursWorked && (
                        <div>Total hours worked: <strong>{shiftDetails.details.totalHoursWorked.toFixed(1)}h</strong></div>
                      )}
                    </div>
                  </div>
                )}

                {/* Shift Timeline */}
                <div>
                  <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">Shift Timeline</h5>
                  <div className="space-y-2">
                    {shiftDetails.shiftDetails.entries.map((entry, idx) => {
                      const isBreak = entry.entryType === 'unpaid_break' || entry.entryType === 'paid_break';
                      const isViolatingSegment = violation.violationType.includes('break') && isBreak;
                      return (
                        <div
                          key={entry.id || idx}
                          className={`flex items-center gap-2 p-2 rounded-md ${
                            isViolatingSegment
                              ? 'bg-red-100 border border-red-300'
                              : isBreak
                                ? 'bg-amber-50 border border-amber-200'
                                : 'bg-green-50 border border-green-200'
                          }`}
                        >
                          <div className={`p-1 rounded-full ${isBreak ? 'bg-amber-200' : 'bg-green-200'}`}>
                            {isBreak ? <Coffee className="w-3 h-3 text-amber-700" /> : <Play className="w-3 h-3 text-green-700" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                isBreak ? 'bg-amber-200 text-amber-800' : 'bg-green-200 text-green-800'
                              }`}>
                                {entry.entryType === 'work' ? 'WORK' : 'BREAK'}
                              </span>
                              {isViolatingSegment && (
                                <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-red-200 text-red-800">
                                  ISSUE
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-700 mt-0.5">
                              {formatDateTime(entry.clockIn)} - {formatDateTime(entry.clockOut)}
                              <span className="mx-1 text-gray-400">•</span>
                              <span className="font-medium">{formatDuration(entry.duration)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-sm text-gray-500">
                Unable to load shift details
              </div>
            )}
          </div>
        )}

        {/* Additional Violation Details */}
        {!isExpanded && violation.description && (
          <p className="text-sm text-gray-700 mb-3 p-2 bg-white bg-opacity-60 rounded-md">
            <span className="font-medium">Details:</span> {violation.description}
          </p>
        )}

        {/* Potential Penalty */}
        {violation.potentialPenalty && parseFloat(violation.potentialPenalty) > 0 && (
          <div className="text-sm text-red-700 font-medium mb-3">
            Potential Penalty: ${parseFloat(violation.potentialPenalty).toFixed(2)}
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={() => onRespond(violation)}
          className="w-full py-3 px-4 bg-primary-600 text-white text-sm font-semibold rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
        >
          {status === 'pending' ? 'Review & Update Status' : 'Change Status'}
        </button>
      </div>
    </div>
  );
};

export default ViolationCard;
